/**
 * Utility functions for chunking transcript segments into time-based groups
 */

/**
 * Groups transcript segments into chunks of approximately the specified duration.
 * Each chunk contains multiple segments that together span roughly the target duration.
 * 
 * @param {Array} segments - Array of transcript segments with start/end times
 * @param {number} targetDurationSeconds - Target duration for each chunk in seconds (default: 300 = 5 minutes)
 * @returns {Array} Array of chunk objects, each containing an array of segments
 */
export function chunkSegmentsByDuration(segments, targetDurationSeconds = 300) {
  if (!segments || segments.length === 0) {
    return [];
  }

  const chunks = [];
  let currentChunk = {
    segments: [],
    startTime: null,
    endTime: null,
    duration: 0,
  };

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    
    // Try different possible timestamp field names (backend uses start_time/end_time)
    const startTime = segment.start_time || segment.startTime || segment.start || 0;
    const endTime = segment.end_time || segment.endTime || segment.end || startTime;

    // Start a new chunk if current is empty
    if (currentChunk.segments.length === 0) {
      currentChunk.startTime = startTime;
      currentChunk.segments.push(segment);
      currentChunk.endTime = endTime;
      currentChunk.duration = currentChunk.endTime - currentChunk.startTime;
    } else {
      // Check if adding this segment would exceed target duration
      const newEndTime = endTime;
      const newDuration = newEndTime - currentChunk.startTime;

      if (newDuration > targetDurationSeconds && currentChunk.segments.length > 0) {
        // Complete current chunk and start a new one
        chunks.push({ ...currentChunk });
        currentChunk = {
          segments: [segment],
          startTime: startTime,
          endTime: endTime,
          duration: endTime - startTime,
        };
      } else {
        // Add segment to current chunk
        currentChunk.segments.push(segment);
        currentChunk.endTime = newEndTime;
        currentChunk.duration = newDuration;
      }
    }
  }

  // Add the last chunk if it has segments
  if (currentChunk.segments.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
}

/**
 * Merges the text from multiple segments within a chunk into a single string.
 * Preserves speaker changes and formatting.
 * 
 * @param {Array} segments - Array of segments to merge
 * @param {boolean} includeSpeakers - Whether to include speaker labels in the merged text
 * @returns {string} Merged text from all segments
 */
export function mergeSegmentText(segments, includeSpeakers = true) {
  if (!segments || segments.length === 0) {
    return '';
  }

  let mergedText = '';
  let previousSpeaker = null;

  for (const segment of segments) {
    const currentSpeaker = segment.speaker || null;
    const text = segment.text || '';

    if (includeSpeakers && currentSpeaker && currentSpeaker !== previousSpeaker) {
      // Add speaker label when speaker changes
      if (mergedText) {
        mergedText += '\n\n';
      }
      mergedText += `${currentSpeaker}: ${text}`;
    } else {
      // Continue with same speaker or no speaker labels
      if (mergedText) {
        mergedText += ' ';
      }
      mergedText += text;
    }

    previousSpeaker = currentSpeaker;
  }

  return mergedText;
}

/**
 * Splits edited chunk text back into individual segments.
 * Attempts to preserve the original segment structure while applying edits.
 * 
 * @param {string} editedText - The edited chunk text
 * @param {Array} originalSegments - The original segments that were merged
 * @returns {Array} Updated segments with edited text
 */
export function splitChunkIntoSegments(editedText, originalSegments) {
  if (!originalSegments || originalSegments.length === 0) {
    return [];
  }

  // For now, we'll implement a simple proportional split
  // This maintains the relative text distribution across segments
  const totalOriginalLength = originalSegments.reduce(
    (sum, seg) => sum + (seg.text || '').length,
    0
  );

  if (totalOriginalLength === 0) {
    // If original had no text, distribute edited text evenly
    const textPerSegment = Math.ceil(editedText.length / originalSegments.length);
    return originalSegments.map((seg, index) => ({
      ...seg,
      text: editedText.substring(
        index * textPerSegment,
        Math.min((index + 1) * textPerSegment, editedText.length)
      ).trim(),
    }));
  }

  // Parse speaker labels if present
  const speakerPattern = /^([^:]+):\s*/;
  let remainingText = editedText;
  const updatedSegments = [];
  let currentPosition = 0;

  for (let i = 0; i < originalSegments.length; i++) {
    const segment = originalSegments[i];
    const originalLength = (segment.text || '').length;
    const proportion = originalLength / totalOriginalLength;
    const targetLength = Math.round(editedText.length * proportion);

    // Extract text for this segment
    let segmentText = '';
    
    if (i === originalSegments.length - 1) {
      // Last segment gets all remaining text
      segmentText = remainingText.trim();
    } else {
      // Find a good break point near the target length
      let breakPoint = currentPosition + targetLength;
      
      // Look for natural break points (sentence end, paragraph)
      const searchRange = 50;
      const searchStart = Math.max(currentPosition, breakPoint - searchRange);
      const searchEnd = Math.min(editedText.length, breakPoint + searchRange);
      const searchText = editedText.substring(searchStart, searchEnd);
      
      // Look for sentence endings
      const sentenceEndMatch = searchText.match(/[.!?]\s+(?=[A-Z])/);
      if (sentenceEndMatch) {
        breakPoint = searchStart + sentenceEndMatch.index + sentenceEndMatch[0].length;
      }
      
      segmentText = editedText.substring(currentPosition, breakPoint).trim();
      currentPosition = breakPoint;
      remainingText = editedText.substring(breakPoint);
    }

    // Check if segment text starts with a speaker label
    const speakerMatch = segmentText.match(speakerPattern);
    if (speakerMatch) {
      updatedSegments.push({
        ...segment,
        speaker: speakerMatch[1],
        text: segmentText.substring(speakerMatch[0].length).trim(),
      });
    } else {
      updatedSegments.push({
        ...segment,
        text: segmentText,
      });
    }
  }

  return updatedSegments;
}

/**
 * Formats a timestamp in seconds to a readable MM:SS or HH:MM:SS format
 * 
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export function formatChunkTimestamp(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}